"use client";

import { Card } from "@/component/override/card.c";
import { useTranslations } from "@/hooks/use-translations";
import { replaceBookingPlaceholders } from "@/lib/booking.utils";
import { CardBody } from "@heroui/react";
import { IconBuilding } from "@tabler/icons-react";
import { CalendarCheck, CheckCircle, Coffee, Lightbulb, MapPin, Users, XCircle } from "lucide-react";
import Cta from "../components/cta";
import Pricing from "../components/pricing";

export default function HowReservationsWork() {
    const t = useTranslations();


    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            {/* Header */}
            <div className="text-center mb-12">
                <h1 className="font-bold mb-4 text-4xl desktop:text-5xl">
                    {t.howReservationsWorkPage?.title}
                </h1>
                <p className="text-foreground/75 text-lg desktop:text-xl max-w-2xl mx-auto italic">
                    {t.howReservationsWorkPage?.subtitle}
                </p>
            </div>

            <div className="space-y-12">
                {/* The Problem Section */}
                <section>
                    <h2 className="text-2xl font-semibold mb-6 flex items-center gap-3">
                        {t.howReservationsWorkPage?.problem?.title}
                    </h2>
                    <p className="text-foreground/80 text-lg leading-relaxed mb-4">
                        {t.howReservationsWorkPage?.problem?.description1}
                    </p>
                    <p className="text-foreground/80 text-lg leading-relaxed">
                        {t.howReservationsWorkPage?.problem?.description2}
                    </p>
                </section>

                {/* Fair Access Solution */}
                <Card className="bg-default-50 rounded-lg p-8">
                    <CardBody>
                        <h2 className="text-2xl font-semibold mb-4 flex items-center gap-3">
                            <CheckCircle className="text-primary" size={28} />
                            {t.howReservationsWorkPage?.solution?.title}
                        </h2>
                        <p className="text-foreground/80 text-lg leading-relaxed mb-6">
                            {replaceBookingPlaceholders(t.howReservationsWorkPage?.solution?.description1)}
                        </p>
                        <p className="text-foreground/80 text-lg leading-relaxed" dangerouslySetInnerHTML={{ __html: replaceBookingPlaceholders(t.howReservationsWorkPage?.solution?.description2) }}>
                        </p>
                    </CardBody>
                </Card>

                {/* How It Works */}
                <section>
                    <h3 className="text-xl font-semibold mb-8 flex items-center gap-3">
                        {t.howReservationsWorkPage?.howItWorks?.title}
                    </h3>

                    <div className="space-y-6">
                        <div>
                            <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                {t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.title}
                            </h4>
                            <p className="text-foreground/80 leading-relaxed">
                                {replaceBookingPlaceholders(t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.description)}
                            </p>
                            {t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details ? (
                                <ul className="mt-3 space-y-2 text-foreground/80 leading-relaxed">
                                    {t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details?.monthlyMembership ? (
                                        <li>{replaceBookingPlaceholders(t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details?.monthlyMembership)}</li>
                                    ) : null}
                                    {t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details?.carnet ? (
                                        <li>{replaceBookingPlaceholders(t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details?.carnet)}</li>
                                    ) : null}
                                    {t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details?.timeSlots ? (
                                        <li>{replaceBookingPlaceholders(t.howReservationsWorkPage?.howItWorks?.guaranteedAccess?.details?.timeSlots)}</li>
                                    ) : null}
                                </ul>
                            ) : null}
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                {t.howReservationsWorkPage?.howItWorks?.weeklyRelease?.title}
                            </h4>
                            <p className="text-foreground/80 leading-relaxed">
                                {replaceBookingPlaceholders(t.howReservationsWorkPage?.howItWorks?.weeklyRelease?.description)}
                            </p>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                {t.howReservationsWorkPage?.howItWorks?.subscriptionTypes?.title}
                            </h4>
                            <Pricing showTitle={false} hideCallToAction={true} />
                        </div>
                    </div>
                </section>

                {/* What to Expect */}
                <section>
                    <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
                        {t.howReservationsWorkPage?.whatToExpect?.title}
                    </h3>

                    <p className="text-foreground/80 leading-relaxed mb-4">
                        {t.howReservationsWorkPage?.whatToExpect?.description}
                    </p>

                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-3">
                                    <Users className="text-primary mt-1 flex-shrink-0" size={16} />
                                    <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.goodFor?.socializing}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Coffee className="text-primary mt-1 flex-shrink-0" size={16} />
                                    <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.goodFor?.quietPlace}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <MapPin className="text-primary mt-1 flex-shrink-0" size={16} />
                                    <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.goodFor?.smallEnvironment}</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <CalendarCheck className="text-primary mt-1 flex-shrink-0" size={16} />
                                    <span className="text-foreground/80" dangerouslySetInnerHTML={{ __html: replaceBookingPlaceholders(t.howReservationsWorkPage?.whatToExpect?.goodFor?.communityEvents) }}></span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <p className="text-foreground/80 leading-relaxed mb-4">
                        {t.howReservationsWorkPage?.whatToExpect?.notIdealFor}
                    </p>

                    <ul className="space-y-2 mb-6">
                        <li className="flex items-start gap-3">
                            <XCircle className="text-danger mt-1 flex-shrink-0" size={16} />
                            <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.badFor?.personalOffice}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <XCircle className="text-danger mt-1 flex-shrink-0" size={16} />
                            <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.badFor?.certainDays}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <XCircle className="text-danger mt-1 flex-shrink-0" size={16} />
                            <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.badFor?.teamFriendly}</span>
                        </li>
                        <li className="flex items-start gap-3">
                            <XCircle className="text-danger mt-1 flex-shrink-0" size={16} />
                            <span className="text-foreground/80">{t.howReservationsWorkPage?.whatToExpect?.badFor?.meetingRooms}</span>
                        </li>
                    </ul>
                </section>

                {/* Pros and Cons Table */}
                <Card>
                    <CardBody>
                        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
                            <Lightbulb className="text-warning" size={24} />
                            {t.howReservationsWorkPage?.prosAndCons?.title}
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="border-b border-default-200">
                                        <th className="text-left p-2 font-semibold text-success">{t.howReservationsWorkPage?.prosAndCons?.pros}</th>
                                        <th className="text-left p-2 font-semibold text-danger">{t.howReservationsWorkPage?.prosAndCons?.cons}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {t.howReservationsWorkPage?.prosAndCons?.items?.map((item: { pro: string; con: string }, index: number) => (
                                        <tr key={index} className={index === t.howReservationsWorkPage.prosAndCons.items.length - 1 ? "border-default-100" : "border-b border-default-200"}>
                                            <td className="p-2 text-foreground/80">{item.pro}</td>
                                            <td className="p-2 text-foreground/80">{item.con}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardBody>
                </Card>

                {/* Why Choose Fair Access */}
                <Card className="bg-primary/5 rounded-lg p-8">
                    <CardBody>
                        <h3 className="text-xl font-semibold mb-4 flex items-center gap-3">
                            <IconBuilding size={24} className="text-primary" />
                            {t.howReservationsWorkPage?.whyChoose?.title}
                        </h3>
                        <p className="text-foreground/80 text-lg leading-relaxed mb-6">
                            {replaceBookingPlaceholders(t.howReservationsWorkPage?.whyChoose?.description)}
                        </p>
                        <ul className="space-y-2">
                            {t.howReservationsWorkPage?.whyChoose?.benefits?.map((benefit: string, index: number) => (
                                <li key={index} className="flex items-start gap-3">
                                    <CheckCircle className="text-primary mt-1 flex-shrink-0" size={16} />
                                    <span className="text-foreground/80" dangerouslySetInnerHTML={{ __html: replaceBookingPlaceholders(benefit) }}></span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-foreground/80 text-lg leading-relaxed mt-6">
                            {replaceBookingPlaceholders(t.howReservationsWorkPage?.whyChoose?.conclusion)}
                        </p>
                    </CardBody>
                </Card>

                {/* Summary */}
                <section>
                    <h3 className="text-xl font-semibold mb-6 flex items-center gap-3">
                        <Lightbulb className="text-yellow-500" size={24} />
                        {t.howReservationsWorkPage?.summary?.title}
                    </h3>
                    <p className="text-foreground/80 text-lg leading-relaxed mb-4">
                        {replaceBookingPlaceholders(t.howReservationsWorkPage?.summary?.description1)}
                    </p>
                    <p className="text-foreground/80 text-lg leading-relaxed">
                        {replaceBookingPlaceholders(t.howReservationsWorkPage?.summary?.description2)}
                    </p>
                </section>

                <Cta />
            </div>
        </div>
    );
}
